import { useParams } from '@reach/router';
import { graphql } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC } from 'react';
import { useQuery } from 'urql';

import { BooleanValue } from '@/bits';
import {
  Card,
  CardBody,
  CardOptions,
  CardOptionsButton,
  Date,
  InlineIconButton,
  Value,
} from '@/components';
import { ExternalLinkIcon, RefreshIcon } from '@/components/icons';
import { useTranslate } from '@/contexts';
import {
  PlayerSumSubVerificationQuery,
  PlayerSumSubVerificationQueryVariables,
} from './__generated__/component';

export const Fragment = graphql`
  fragment SanityPlayerSumSubVerificationBlock on SanityPlayerSumSubVerificationBlock {
    title {
      ...SanityLocaleString
    }
  }
`;

const QUERY = gql`
  query PlayerSumSubVerification($playerId: ID!) {
    player(playerId: $playerId) {
      id
      kycCheckStatus {
        sumSubEndUserId
      }
      playerKycHistory(
        kycProvider: SumSub
        getOnlyLatestKycPerPlayer: true
        orderBy: processedAt
        desc: true
        first: 1
      ) {
        edges {
          node {
            id
            kycProvider
            processedAt
          }
        }
      }
    }
  }
`;

const PlayerSumSubVerificationBlock: FC<{
  block: Queries.SanityPlayerSumSubVerificationBlockFragment;
}> = ({ block }) => {
  const { t } = useTranslate();
  const { playerId } = useParams();

  const [{ data, fetching }, refresh] = useQuery<
    PlayerSumSubVerificationQuery,
    PlayerSumSubVerificationQueryVariables
  >({
    query: QUERY,
    variables: {
      playerId,
    },
  });

  const latestVerification =
    data?.player.playerKycHistory?.edges?.[0]?.node ?? undefined;
  const sumSubEndUserId = data?.player.kycCheckStatus?.sumSubEndUserId;

  return (
    <Card
      size="lg"
      title={t(block.title)}
      options={
        <CardOptions>
          <CardOptionsButton
            className="flex"
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
        </CardOptions>
      }
    >
      <CardBody>
        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          <BooleanValue
            fetching={fetching}
            title="Verified by SumSub"
            value={Boolean(latestVerification)}
          />
          <Value fetching={fetching} title="Verification Date">
            {latestVerification && (
              <Date date={latestVerification.processedAt} />
            )}
          </Value>
          <Value fetching={fetching} title="SumSub Profile">
            {sumSubEndUserId && (
              <InlineIconButton>
                <a
                  href={`${process.env.SUMSUB_APPLICANT_PROFILE}externalUserId=${sumSubEndUserId}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExternalLinkIcon />
                </a>
              </InlineIconButton>
            )}
          </Value>
        </div>
      </CardBody>
    </Card>
  );
};

export default PlayerSumSubVerificationBlock;
